// Native UI for the timeline tool (Timeline Lane 1.0.0) — a separate
// tool alongside data-sources-registry, deliberately the same shape as
// DataSourcesRegistry.tsx (copied + adapted, NOT shared — DSR stays
// untouched). Renders ONE TAB PER REGISTERED REGISTRY FILE
// (storage.registryFiles in tools/timeline-lane.tool.json) and
// dispatches table rendering by the file's declared schema:
//   - events-v1          → <EventsTable/> (242 deep-time events,
//                          BCE-aware dates)
//   - audit-timeline-v1  → <AuditTimelineTable/> (second-precision
//                          workspace audit log)
//   - timeline-marker-v1 → <TimelineMarkersTable/> (shared by the
//                          Markers + Future Windows tabs; the column
//                          values key off marker.display line/window)
//
// The four YAML files are shared with the standalone D3 timeline viz
// at code-tron/timeline-v010-precise/ — this page is read-only and the
// files' events[] shape must not be reshaped. Slice C (write path) is
// a follow-up ticket.
//
// CTSK-253 slice B — a 5th SYNTHETIC tab ("Timeline") renders the
// embedded D3 viz via iframe. "Synthetic" because it does not
// correspond to a single registryFiles[] entry: the viz aggregates the
// three second-precision files (audit-timeline-events.yaml +
// markers.yaml + future-events.yaml, fetched through the cap-gated
// GET /api/v1/timeline-viz/:filename proxy) into one chart. It is a
// hardcoded case in this page component, NOT a .tool.json entry — the
// wizard (CTSK-252) should treat viz tabs as page-level, not
// manifest-level. As of CTSK-260 (0.1.91) the viz also loads
// events.yaml (deep-time historical) as an optional 4th source; note
// the viz's parser skips BCE negative-year entries with console.warn,
// so the Events tab's table stays the BCE-complete rendering.
//
// CTSK-261 (0.1.92) — dataset selector for the viz tab: a checkbox
// row above the iframe toggles which registry files feed the chart
// ("just the audit trail", "just deep-time history", …). The selection
// is passed into the embedded viz copy via `?sources=` (comma-separated
// file bases, minus `.yaml`); the viz filters CONFIG.dataSources before
// D3 runs. Persisted per-operator in localStorage
// (`timeline-lane:viz-sources`); toggling remounts the iframe via
// key={sources.join(',')}. Client-side only — the proxy whitelist is
// unchanged and still enforced per request.
//
// CTSK-262 (0.1.91) — fullscreen mode for the viz tab: an "Expand ⤢"
// button pops the iframe wrapper to the full viewport with in-page CSS
// only (.timeline-lane-viz-fullscreen — position:fixed inset:0; NOT
// the browser Fullscreen API, no dedicated route). The iframe DOM node
// is preserved across the toggle (only the wrapper's class and the
// iframe's height style change), so the viz keeps its state. Exit via
// the overlay button or ESC; the flag is plain useState — session-only
// by design, never persisted.
//
// CTSK-320 (0.1.131) — viz chrome refinement. The CTSK-261 inline
// checkbox row and the strip-level Expand button collapsed into:
//   - a compact ACTION RAIL at the top of the viz container — left: a
//     "sources: N of M" chip; right: EditLockToggle + Expand ⤢ + the ⚙
//     drawer toggle;
//   - a right-side SETTINGS DRAWER (TimelineVizSettingsDrawer.tsx)
//     holding the dataset toggles (+ a slot for future settings).
//     Closed by default; open state persists to
//     localStorage('timeline-lane:viz-chrome') as {drawerOpen}.
// The rail + drawer render INSIDE the viz container, so fullscreen
// (CTSK-262) keeps both accessible. Drawer open/close is a pure
// transform toggle — the iframe node is never remounted by it; only a
// SOURCE toggle remounts (key={sources.join(',')}, CTSK-261 semantics
// unchanged). Also as of CTSK-320 the viz gained a 5th source,
// sessions-timeline.yaml (Zulip session-cycle events, CTSK-320).
//
// CTSK-322/323 (0.1.132) — chrome v2. CTSK-320's drawer-for-sources did
// not survive first contact and is SUPERSEDED here:
//   - the dataset toggles move back above the iframe as an always-visible
//     CHIP STRIP (TimelineVizChipStrip.tsx), which also carries the
//     lock/expand/⚙ rail on its right. There is no way to hide the source
//     list any more — "which data is on the chart" is never hidden state.
//   - the drawer keeps its mount + transform contract but loses every
//     source control; it is retitled "Chrome preferences" with a
//     placeholder body.
//   - the viz's Live Lab (CTSK-322/323) mirrors its state up over
//     same-origin postMessage: `timeline-lab:counts` (post-validate row
//     counts for the chips), `timeline-lab:source-{added,removed}` (LAB
//     chips) and `timeline-lab:schema-{added,removed}` (the `[schema]`
//     chip badge). The page posts `timeline-lab:open-drop-zone` back down
//     for the `+ Lab` chip and the `[schema]` badge. Every listener is
//     origin-guarded and detached on unmount; lab state is session-only
//     and deliberately never persisted (a reload clears the bench, which
//     matches what the viz itself does with its blob: URLs).
//
// Auth: requires tools:read_registry to view; fetches via
// /tools/:id/registry?path=<file> → documentId → content. The iframe
// is same-origin, so the session cookie flows into the viz's fetches
// natively — no sandbox attr. The postMessage bridge above is chrome
// state only; it carries no data and no credentials.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Brush, Pencil } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { resolveRegistryFiles } from '@captain-tasko/shared';
import type { RegistryFileEntry } from '@captain-tasko/shared';
import { apiFetch, apiList, apiSingle } from '../lib/api.js';
import { TabHeaderCanonicalLink } from '../components/TabHeaderCanonicalLink.js';
import { HelpLink } from '../components/HelpLink.js';
import { ActivityPanel } from '../components/ActivityPanel.js';
import { EditLockToggle } from '../components/EditLockToggle.js';
import { DocumentYamlEditModal } from '../components/DocumentYamlEditModal.js';
import { CleanRegistryModal } from '../components/CleanRegistryModal.js';
import { ToolRegistryUploadButton } from '../components/ToolRegistryUploadButton.js';
import { ToolActivityTab } from '../components/ToolActivityTab.js';
import { EventsTable } from '../components/timeline/EventsTable.js';
import { AuditTimelineTable } from '../components/timeline/AuditTimelineTable.js';
import { TimelineMarkersTable } from '../components/timeline/TimelineMarkersTable.js';
import { FutureWindowsTable } from '../components/timeline/FutureWindowsTable.js';
import { TimelineVizSettingsDrawer } from './TimelineVizSettingsDrawer.js';
import { TimelineVizChipStrip } from '../components/timeline/TimelineVizChipStrip.js';
import type { LabChipSource } from '../components/timeline/TimelineVizChipStrip.js';

interface ToolRow {
  id: string;
  type: string;
  nestName: string | null;
  registryContainerId: string;
  registryContainerSlug: string | null;
  registryPath: string;
  // storage block in manifest shape; feed to resolveRegistryFiles()
  // for the tab list. Optional so the page degrades to the legacy
  // single-file shape against an older API.
  storage?: {
    containerName: string | null;
    registryPath: string | null;
    registryFiles: RegistryFileEntry[] | null;
  };
}

interface RegistryPointer {
  toolId: string;
  registryPath: string;
  documentId?: string;
  contentUrl?: string;
  contents?: null;
  note?: string;
}

// Operator preference: reload lands you back on your tab.
const ACTIVE_TAB_STORAGE_KEY = 'timeline-lane:activeTab';

// CTSK-253 slice B — the synthetic viz tab. Its slug shares the
// localStorage keyspace with the file paths above (they can never
// collide: registry paths always carry a .yaml extension), so the
// reload-roundtrip works for the viz tab with zero extra plumbing.
export const VIZ_TAB_SLUG = 'timeline';
const VIZ_TAB_LABEL = 'Timeline';

// CTSK-290 — synthetic Activity tab (the per-tool audit trail from
// GET /tools/:id/activity). Same synthetic-tab pattern as the viz tab;
// the marker slug can never collide with a registry path (.yaml).
export const ACTIVITY_TAB_SLUG = '__activity__';
const ACTIVITY_TAB_LABEL = 'Activity';

/** The embedded D3 viz — a captain-tasko copy of the standalone
 *  code-tron/timeline-v010-precise/ chart, served from
 *  public/timeline-viz/. Explicit index.html (not the bare directory)
 *  so vite dev and the nginx SPA fallback resolve it identically. */
export const VIZ_IFRAME_SRC = '/timeline-viz/index.html';

// CTSK-261 — the viz dataset selector. Keys are the registry
// filenames minus `.yaml` (MUST stay in lockstep with the whitelist in
// apps/api/src/routes/timeline-viz.ts and the embedded viz's
// CONFIG.dataSources URLs); the labels mirror the tab strip. Canonical
// order here is the stable `?sources=` order.
export const VIZ_SOURCES_STORAGE_KEY = 'timeline-lane:viz-sources';
export const VIZ_SOURCE_OPTIONS = [
  { key: 'events', label: 'Events (historical)' },
  { key: 'audit-timeline-events', label: 'Audit Timeline' },
  { key: 'markers', label: 'Markers' },
  { key: 'future-events', label: 'Future Windows' },
  // CTSK-320 — session-cycle communication events (Zulip `sessions`
  // stream). timeline-marker-v1 shaped; the viz renders them as lane
  // events via its treatAsEvents flag.
  { key: 'sessions-timeline', label: 'Sessions' },
] as const;
const ALL_VIZ_SOURCE_KEYS: string[] = VIZ_SOURCE_OPTIONS.map((o) => o.key);

// CTSK-320 — viz chrome state (the settings drawer). Separate key from
// the CTSK-261 source selection: chrome is layout preference, sources
// are data selection. JSON envelope so future chrome flags can join
// without a key migration. Default (absent / unparseable) = drawer
// CLOSED — the chart is the point of the tab.
export const VIZ_CHROME_STORAGE_KEY = 'timeline-lane:viz-chrome';

function loadStoredVizChrome(): { drawerOpen: boolean } {
  if (typeof window === 'undefined') return { drawerOpen: false };
  try {
    const raw = window.localStorage.getItem(VIZ_CHROME_STORAGE_KEY);
    if (!raw) return { drawerOpen: false };
    const parsed = JSON.parse(raw) as { drawerOpen?: unknown };
    return { drawerOpen: parsed?.drawerOpen === true };
  } catch {
    return { drawerOpen: false };
  }
}

function storeVizChrome(chrome: { drawerOpen: boolean }) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(VIZ_CHROME_STORAGE_KEY, JSON.stringify(chrome));
  } catch {
    // Private-mode / quota failures are non-fatal — the drawer state
    // just won't persist across reloads (same stance as storeTab).
  }
}

/** Stored selection, or the all-5 default when nothing usable is
 *  stored. Unknown keys (stale/hand-edited values) are dropped and the
 *  canonical order is always re-imposed, so the `?sources=` string —
 *  and with it the iframe remount key — is stable for a given set.
 *
 *  CTSK-322: this auto-heal is the fix for the CTSK-320 regression. A
 *  stored value whose intersection with ALL_VIZ_SOURCE_KEYS is EMPTY —
 *  `''`, `'bogus,keys'`, or a set of keys renamed out from under it —
 *  falls back to ALL, never to nothing. Combined with the chip strip
 *  being unhideable, a blank chart can no longer be reached by accident. */
function loadStoredVizSources(): string[] {
  if (typeof window === 'undefined') return [...ALL_VIZ_SOURCE_KEYS];
  try {
    const raw = window.localStorage.getItem(VIZ_SOURCES_STORAGE_KEY);
    if (!raw) return [...ALL_VIZ_SOURCE_KEYS];
    const stored = new Set(raw.split(',').filter(Boolean));
    const valid = ALL_VIZ_SOURCE_KEYS.filter((k) => stored.has(k));
    return valid.length > 0 ? valid : [...ALL_VIZ_SOURCE_KEYS];
  } catch {
    return [...ALL_VIZ_SOURCE_KEYS];
  }
}

// ── CTSK-322/323 — the viz → page postMessage contract ────────────────
//
// Emitted by apps/web/public/timeline-viz/index.html (upstream:
// code-tron/timeline-lane/index.html). `targetKey` is the registry file
// base for a registered source or the dropped filename for a lab source —
// labels differ in case between the two codebases, keys do not.
export const LAB_MESSAGE_TYPES = {
  counts: 'timeline-lab:counts',
  sourceAdded: 'timeline-lab:source-added',
  sourceRemoved: 'timeline-lab:source-removed',
  schemaAdded: 'timeline-lab:schema-added',
  schemaRemoved: 'timeline-lab:schema-removed',
  openDropZone: 'timeline-lab:open-drop-zone',
  removeSource: 'timeline-lab:remove-source',
} as const;

interface LabSourceMessage {
  type: string;
  payload?: {
    label?: string;
    filename?: string;
    count?: number;
    hasSchemaOverride?: boolean;
    targetKey?: string;
    targetLabel?: string;
    counts?: Record<string, number>;
  };
}

function storeVizSources(sources: string[]) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(VIZ_SOURCES_STORAGE_KEY, sources.join(','));
  } catch {
    // Private-mode / quota failures are non-fatal — the selection just
    // won't persist across reloads (same stance as storeTab).
  }
}

function loadStoredTab(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(ACTIVE_TAB_STORAGE_KEY);
  } catch {
    return null;
  }
}

function storeTab(path: string) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, path);
  } catch {
    // Private-mode / quota failures are non-fatal — the tab just
    // won't persist across reloads.
  }
}

// ── Component ─────────────────────────────────────────────────────────

export function TimelineLane({ onNavigate }: { onNavigate: (p: string) => void }) {
  // 1. Find the installed tool by type. If multiple installs exist
  //    (one per nest), the operator picks via a dropdown.
  const toolsQ = useQuery({
    queryKey: ['tools', { type: 'timeline' }],
    queryFn: () => apiList<ToolRow>('/tools', { query: { type: 'timeline' } }),
  });
  const tools = toolsQ.data?.data ?? [];
  const [selectedToolId, setSelectedToolId] = useState<string>('');
  useEffect(() => {
    if (!selectedToolId && tools.length > 0) setSelectedToolId(tools[0]!.id);
  }, [tools, selectedToolId]);
  // Fall back to the first install while the selectedToolId effect
  // hasn't flushed yet. Without this there is one committed frame
  // after the tools query resolves where `tool` is undefined → files
  // is [] → the tab strip renders ONLY the synthetic viz tab. That
  // 1-tab frame is a visible flash for users and a race for tests
  // polling the strip (the 0.1.91 CI flake: getAllByRole('tab')
  // observed mid-flash, then all[4] was undefined).
  const tool = tools.find((t) => t.id === selectedToolId) ?? tools[0];

  // 2. Resolve the registered file list → tab strip.
  const files = useMemo<RegistryFileEntry[]>(
    () => (tool ? resolveRegistryFiles(tool.storage ?? { registryPath: tool.registryPath }) : []),
    [tool],
  );

  // 3. Active tab — keyed by file path (or the synthetic viz slug),
  //    persisted to localStorage so a reload lands the operator back
  //    on their tab. A stored path that no longer matches a registered
  //    file falls back to the first entry.
  const [activeTab, setActiveTab] = useState<string | null>(() => loadStoredTab());
  const isVizTab = activeTab === VIZ_TAB_SLUG;
  const isActivityTab = activeTab === ACTIVITY_TAB_SLUG;
  const activeFile = isVizTab || isActivityTab
    ? null
    : files.find((f) => f.path === activeTab) ?? files[0] ?? null;

  // CTSK-262 — viz fullscreen flag. Plain state, deliberately NOT
  // persisted (a reload always lands back in normal chrome). Leaving
  // the viz tab drops fullscreen too, so returning later never
  // re-enters it unexpectedly.
  const [isFullscreen, setIsFullscreen] = useState(false);

  // CTSK-268 slice B — per-tab edit lock. Default LOCKED (tables stay
  // display-only); unlocking is a deliberate per-visit signal and is
  // NEVER persisted — a reload or tab switch always re-locks. The
  // unlocked affordance is the whole-document "Edit YAML" modal, not
  // per-row editing: no existing endpoint mutates a single YAML list
  // row (PATCH /documents/:id replaces content wholesale, the tools
  // registry write route is stubbed, and /registry/append is
  // append-only) — see DocumentYamlEditModal.tsx for the full why.
  const [editUnlocked, setEditUnlocked] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  // CTSK-256 — the cleaning modal, gated behind the same unlock as the
  // Edit YAML affordance (unlock → Clean button appears).
  const [showCleanModal, setShowCleanModal] = useState(false);

  // CTSK-261 — which registry files feed the viz. Lazy-initialized
  // from localStorage (all 4 when nothing stored); every toggle
  // persists + re-imposes canonical order, and the iframe below
  // remounts via key={vizSources.join(',')} so the chart reloads with
  // the new `?sources=`.
  const [vizSources, setVizSources] = useState<string[]>(() => loadStoredVizSources());

  const toggleVizSource = (key: string) => {
    const next = vizSources.includes(key)
      ? vizSources.filter((k) => k !== key)
      : ALL_VIZ_SOURCE_KEYS.filter((k) => vizSources.includes(k) || k === key);
    setVizSources(next);
    storeVizSources(next);
  };

  const showAllVizSources = () => {
    const next = [...ALL_VIZ_SOURCE_KEYS];
    setVizSources(next);
    storeVizSources(next);
  };

  // ── CTSK-322/323 — mirrored Live Lab state ─────────────────────────
  //
  // All THREE of these are session-only by design. The viz holds its lab
  // files as blob: URLs that die with the document, so persisting the
  // chrome's view of them would resurrect chips pointing at nothing.
  const [labSources, setLabSources] = useState<LabChipSource[]>([]);
  const [vizCounts, setVizCounts] = useState<Record<string, number | undefined>>({});
  /** key → dropped schema filename, for the `[schema]` chip badge. */
  const [schemaOverrides, setSchemaOverrides] = useState<Record<string, string | undefined>>({});

  const vizFrameRef = useRef<HTMLIFrameElement | null>(null);

  /** Post down into the viz. Same-origin target — the iframe is served
   *  from this origin, so anything else is not our frame. */
  const postToViz = useCallback((type: string, payload: Record<string, unknown>) => {
    vizFrameRef.current?.contentWindow?.postMessage({ type, payload }, window.location.origin);
  }, []);

  const openLabZone = useCallback(
    (section: 'data' | 'schema') => postToViz(LAB_MESSAGE_TYPES.openDropZone, { section }),
    [postToViz],
  );

  /** The LAB chip's ×. The viz owns the blob: URL and the CONFIG entry,
   *  so removal is a REQUEST, not a local state edit — the chip
   *  disappears when the viz echoes `timeline-lab:source-removed`. That
   *  keeps the chip strip incapable of showing a lane that still exists
   *  (or hiding one that does). */
  const handleLabRemove = useCallback(
    (filename: string) => postToViz(LAB_MESSAGE_TYPES.removeSource, { filename }),
    [postToViz],
  );

  // CTSK-322 M — the inbound listener. Origin-guarded, attached once for
  // the page's lifetime and detached on unmount.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data as LabSourceMessage | null;
      if (!data || typeof data !== 'object' || typeof data.type !== 'string') return;
      const payload = data.payload ?? {};

      switch (data.type) {
        case LAB_MESSAGE_TYPES.counts: {
          if (payload.counts && typeof payload.counts === 'object') {
            setVizCounts((prev) => ({ ...prev, ...payload.counts }));
          }
          break;
        }
        case LAB_MESSAGE_TYPES.sourceAdded: {
          const filename = payload.filename;
          if (!filename) break;
          const next: LabChipSource = {
            filename,
            label: payload.label ?? `LAB · ${filename}`,
            count: payload.count ?? 0,
            hasSchemaOverride: payload.hasSchemaOverride === true,
          };
          // Re-dropping the same filename replaces its chip rather than
          // stacking a duplicate.
          setLabSources((prev) => [...prev.filter((l) => l.filename !== filename), next]);
          break;
        }
        case LAB_MESSAGE_TYPES.sourceRemoved: {
          const filename = payload.filename;
          if (!filename) break;
          setLabSources((prev) => prev.filter((l) => l.filename !== filename));
          setSchemaOverrides((prev) => {
            if (!(filename in prev)) return prev;
            const next = { ...prev };
            delete next[filename];
            return next;
          });
          break;
        }
        case LAB_MESSAGE_TYPES.schemaAdded: {
          const key = payload.targetKey;
          if (!key || !payload.filename) break;
          setSchemaOverrides((prev) => ({ ...prev, [key]: payload.filename }));
          setLabSources((prev) =>
            prev.map((l) => (l.filename === key ? { ...l, hasSchemaOverride: true } : l)),
          );
          break;
        }
        case LAB_MESSAGE_TYPES.schemaRemoved: {
          const key = payload.targetKey;
          if (!key) break;
          setSchemaOverrides((prev) => {
            if (!(key in prev)) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
          });
          setLabSources((prev) =>
            prev.map((l) => (l.filename === key ? { ...l, hasSchemaOverride: false } : l)),
          );
          break;
        }
        default:
          break;
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Leaving the viz tab (or changing the source set, which remounts the
  // iframe) drops the mirrored lab state: the new document has no blob:
  // URLs, so keeping the chips would be a lie.
  const vizSourcesKey = vizSources.join(',');
  useEffect(() => {
    setLabSources([]);
    setSchemaOverrides({});
  }, [activeTab, vizSourcesKey]);

  // CTSK-320 — settings drawer flag. Lazy-initialized from
  // localStorage (closed when nothing stored); every toggle persists.
  // Opening/closing NEVER touches vizSources, so the iframe key — and
  // with it the mounted iframe node — survives every drawer toggle.
  const [drawerOpen, setDrawerOpen] = useState<boolean>(() => loadStoredVizChrome().drawerOpen);

  const setDrawer = (open: boolean) => {
    setDrawerOpen(open);
    storeVizChrome({ drawerOpen: open });
  };

  const selectTab = (path: string) => {
    setActiveTab(path);
    storeTab(path);
    setIsFullscreen(false);
    // CTSK-268 slice B — switching tabs re-locks. Unlock is a per-tab,
    // per-visit signal; it never follows you across tabs.
    setEditUnlocked(false);
    setShowEditModal(false);
    setShowCleanModal(false);
  };

  // ESC exits fullscreen. Listener only exists while fullscreen —
  // no keydown cost the rest of the time.
  useEffect(() => {
    if (!isFullscreen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsFullscreen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isFullscreen]);

  // 4. Resolve the registry pointer for the active tab → document id.
  //    Keyed by [containerId, path] so React Query dedupes per file
  //    and switching back to a visited tab doesn't re-fetch.
  const containerId = tool?.registryContainerId;
  const ptrQ = useQuery({
    queryKey: ['tool-registry', containerId, activeFile?.path],
    queryFn: () =>
      apiSingle<RegistryPointer>(`/tools/${selectedToolId}/registry`, {
        query: { path: activeFile!.path },
      }),
    enabled: !!selectedToolId && !!activeFile,
  });

  // 5. Fetch the YAML bytes for the active tab.
  const yamlQ = useQuery({
    queryKey: ['tool-registry-content', containerId, activeFile?.path, ptrQ.data?.documentId],
    queryFn: async () => {
      if (!ptrQ.data?.documentId) return '';
      const text = await apiFetch<string>(`/documents/${ptrQ.data.documentId}/content`, { raw: true } as never)
        .catch(async () => {
          // The /content endpoint streams text/* directly — fetch raw.
          const res = await fetch(`/api/v1/documents/${ptrQ.data!.documentId}/content`, {
            credentials: 'include',
          });
          if (!res.ok) throw new Error(`Failed to load registry: ${res.status}`);
          return res.text();
        });
      return typeof text === 'string' ? text : '';
    },
    enabled: !!ptrQ.data?.documentId,
  });

  // Schema → table dispatch for the active tab. timeline-marker-v1 is
  // shared by two tabs (markers.yaml + future-events.yaml) — the file
  // path picks the variant (CTSK-267): future-* gets the Future
  // Windows filter set (track + timing) instead of scope + kind.
  const renderTable = (schema: RegistryFileEntry['schema'], yamlText: string, path?: string) => {
    switch (schema) {
      case 'events-v1':          return <EventsTable yamlText={yamlText} />;
      case 'audit-timeline-v1':  return <AuditTimelineTable yamlText={yamlText} />;
      case 'timeline-marker-v1':
        return path?.startsWith('future')
          ? <FutureWindowsTable yamlText={yamlText} />
          : <TimelineMarkersTable yamlText={yamlText} />;
      default:
        return (
          <div style={{ padding: 16, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', color: 'var(--text-muted)', fontSize: 13 }}>
            No table renderer for schema <code style={{ fontFamily: 'var(--font-mono)' }}>{schema}</code> on this
            page — check <code>storage.registryFiles</code> in <code>tools/timeline-lane.tool.json</code>.
          </div>
        );
    }
  };

  // ── Render ─────────────────────────────────────────────────────────

  if (toolsQ.isLoading) {
    return <div style={{ padding: 20, color: 'var(--text-muted)' }}>Loading tool…</div>;
  }
  // "Tool isn't installed" guard is still first — before the tabs.
  if (tools.length === 0) {
    return (
      <div style={{ padding: 20, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', color: 'var(--text-muted)', fontSize: 13, maxWidth: 720, margin: 20 }}>
        <strong>The timeline tool isn't installed in this org.</strong>
        <p style={{ margin: '8px 0 0 0' }}>
          Install it from{' '}
          <button
            type="button"
            onClick={() => onNavigate('/settings?section=tools')}
            style={{ background: 'none', border: 'none', color: 'var(--accent-primary)', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
          >
            Settings → Tools
          </button>{' '}
          using <code>tools/timeline-lane.tool.json</code>.
        </p>
      </div>
    );
  }

  return (
    // CTSK-324 — uncap on wide monitors. Two stacked caps were throttling
    // this page: this wrapper at 1400px AND the embedded viz's own <main>
    // at 1900px. Lifting one alone does nothing — the narrower always
    // wins — so both moved in the same ship (the viz side is upstream in
    // code-tron/timeline-lane). The min() keeps the gutter on screens
    // narrower than the cap, and 3200px stays a cap rather than a removal:
    // past that the chart's lane labels sit too far from their points.
    // Deliberately NOT touched: .timeline-lane-viz-fullscreen (already
    // position:fixed inset:0), and the maxWidth 720/420 content caps
    // below — those are legibility limits on prose, not layout.
    <div style={{ padding: 24, maxWidth: 'min(3200px, calc(100vw - 24px))', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: '-0.01em' }}>Timeline Lane</h1>
          <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>
            Canonical source of truth: <code style={{ fontFamily: 'var(--font-mono)', background: 'var(--surface-sunken)', padding: '1px 5px', borderRadius: 4 }}>
              {isVizTab
                // The viz aggregates its five source files (events.yaml
                // since CTSK-260, sessions-timeline.yaml since CTSK-320)
                // — no single canonical path to point at.
                ? 'audit-timeline-events.yaml + markers.yaml + future-events.yaml + events.yaml + sessions-timeline.yaml'
                : isActivityTab
                  // CTSK-290 — the Activity tab reads audit_log, not a file.
                  ? 'audit_log (per-tool activity)'
                  : activeFile?.path ?? tool?.registryPath ?? 'events.yaml'}
            </code>
            {' '}· shared with the D3 viz at <code style={{ fontFamily: 'var(--font-mono)' }}>code-tron/timeline-lane/</code>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* CTSK-355 — tool-detail help link. No Timeline-specific help
              doc yet, so this maps to the general tools doc (missing-doc
              set tracked on the ship report for follow-up filing). */}
          {/* CTSK-382 — dedicated Timeline Lane help doc (was the generic tools doc). */}
          <HelpLink pageName="Timeline Lane" slug="timeline-lane" onNavigate={onNavigate} />
          {/* CTSK-316 — upload-here affordance: standard upload dialog
              with the tool's bound container preselected and filenames
              restricted to the registered registryFiles[].path list.
              The synthetic viz tab has no file of its own — the dialog
              then preselects the first registered file instead. */}
          {tool && (
            <ToolRegistryUploadButton
              toolId={tool.id}
              containerId={tool.registryContainerId}
              containerLabel={tool.registryContainerSlug}
              registryFiles={files}
              activePath={isVizTab || isActivityTab ? undefined : activeFile?.path}
            />
          )}
          {/* Tool picker — only shows when >1 install */}
          {tools.length > 1 && (
            <select
              value={selectedToolId}
              onChange={(e) => setSelectedToolId(e.target.value)}
              style={{
                padding: '6px 10px',
                fontSize: 13,
                border: '1px solid var(--border-strong)',
                borderRadius: 4,
                background: 'var(--surface-raised)',
              }}
            >
              {tools.map((t) => (
                <option key={t.id} value={t.id}>{t.nestName ?? '(no nest)'} · {t.registryContainerSlug}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Tab strip — one per registered registry file */}
      <div
        role="tablist"
        aria-label="Registry files"
        style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--border-subtle)', marginBottom: 16, flexWrap: 'wrap' }}
      >
        {files.map((f) => {
          const isActive = !isVizTab && !isActivityTab && f.path === activeFile?.path;
          return (
            <button
              key={f.path}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => selectTab(f.path)}
              title={f.path}
              style={{
                padding: '8px 16px',
                fontSize: 13,
                fontWeight: isActive ? 600 : 400,
                background: 'none',
                border: 'none',
                borderBottom: isActive ? '2px solid var(--accent-primary)' : '2px solid transparent',
                color: isActive ? 'var(--text-primary)' : 'var(--text-muted)',
                cursor: 'pointer',
                marginBottom: -1,
              }}
            >
              {f.label}
            </button>
          );
        })}
        {/* Synthetic viz tab (CTSK-253 slice B) — LAST, after the
            file-driven tabs: table exploration first, chart summary
            second. Rendered whenever the tool resolves; it is not a
            registryFiles[] entry (see header comment). */}
        <button
          type="button"
          role="tab"
          aria-selected={isVizTab}
          onClick={() => selectTab(VIZ_TAB_SLUG)}
          title="Embedded D3 timeline visualization"
          style={{
            padding: '8px 16px',
            fontSize: 13,
            fontWeight: isVizTab ? 600 : 400,
            background: 'none',
            border: 'none',
            borderBottom: isVizTab ? '2px solid var(--accent-primary)' : '2px solid transparent',
            color: isVizTab ? 'var(--text-primary)' : 'var(--text-muted)',
            cursor: 'pointer',
            marginBottom: -1,
          }}
        >
          {VIZ_TAB_LABEL}
        </button>
        {/* CTSK-290 — synthetic Activity tab, LAST: the per-tool
            install/troubleshooting audit trail. */}
        <button
          type="button"
          role="tab"
          aria-selected={isActivityTab}
          onClick={() => selectTab(ACTIVITY_TAB_SLUG)}
          title="Install / troubleshooting activity log for this tool"
          style={{
            padding: '8px 16px',
            fontSize: 13,
            fontWeight: isActivityTab ? 600 : 400,
            background: 'none',
            border: 'none',
            borderBottom: isActivityTab ? '2px solid var(--accent-primary)' : '2px solid transparent',
            color: isActivityTab ? 'var(--text-primary)' : 'var(--text-muted)',
            cursor: 'pointer',
            marginBottom: -1,
          }}
        >
          {ACTIVITY_TAB_LABEL}
        </button>
        {/* CTSK-262's strip-level Expand button moved into the viz
            container's action rail (CTSK-320) — the strip is tabs-only
            again. */}
      </div>

      {/* CTSK-268 slice A — per-tab admin strip. Renders for the four
          FILE tabs only (the synthetic viz tab aggregates all four
          files — no single canonical target to link). The documentId
          comes from the same registry-pointer query the tab body uses;
          while it's in flight, or when the file isn't uploaded into
          the bound container yet, the filename chip renders unlinked. */}
      {!isVizTab && activeFile && (
        <div
          data-testid="tab-admin-strip"
          style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}
        >
          <TabHeaderCanonicalLink path={activeFile.path} documentId={ptrQ.data?.documentId} />
          {/* CTSK-268 slice B — lock toggle (right-aligned) + the
              edit affordance it reveals. The Edit button needs a
              resolved documentId; an un-uploaded file has nothing to
              edit and says so instead. */}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            {editUnlocked && (
              ptrQ.data?.documentId ? (
                <>
                  {/* CTSK-256 — cleaning affordance, revealed by the same
                      unlock as Edit YAML. */}
                  <button
                    type="button"
                    onClick={() => setShowCleanModal(true)}
                    title={`Clean ${activeFile.path} (dedupe / normalize / migrate — preview first)`}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', fontSize: 12, border: '1px solid var(--border-strong)', borderRadius: 6, background: 'var(--surface-raised)', color: 'var(--text-primary)', fontWeight: 600, cursor: 'pointer' }}
                  >
                    <Brush size={12} aria-hidden /> Clean
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowEditModal(true)}
                    title={`Edit the raw YAML of ${activeFile.path} (whole-document edit)`}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', fontSize: 12, border: 'none', borderRadius: 6, background: 'var(--accent-primary)', color: '#fff', fontWeight: 600, cursor: 'pointer' }}
                  >
                    <Pencil size={12} aria-hidden /> Edit YAML
                  </button>
                </>
              ) : (
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  Nothing to edit — file not uploaded yet
                </span>
              )
            )}
            <EditLockToggle
              locked={!editUnlocked}
              onToggle={() => { setEditUnlocked((u) => !u); setShowEditModal(false); setShowCleanModal(false); }}
            />
          </div>
        </div>
      )}

      {/* CTSK-268 slice C — collapsible "Recent changes" panel above
          the table. Audit-log-derived (GET /documents/:id/activity —
          document_versions has no serving endpoint) and cap-gated on
          audit:view inside the component; renders nothing without the
          cap or before the file resolves to a document. */}
      {!isVizTab && activeFile && ptrQ.data?.documentId && (
        <div style={{ marginBottom: 12 }}>
          <ActivityPanel
            documentId={ptrQ.data.documentId}
            storageKey={`timeline-lane:activity:${activeFile.path}`}
          />
        </div>
      )}

      {/* Per-tab body */}
      {isActivityTab && tool ? (
        // CTSK-290 — per-tool audit trail.
        <ToolActivityTab toolId={tool.id} />
      ) : isVizTab ? (
        // CTSK-253 slice B — the embedded D3 viz. Same-origin iframe:
        // the session cookie flows into the viz's /api/v1/timeline-viz/*
        // fetches natively, so NO sandbox attribute (sandboxing would
        // sever cookie access and blank the chart). Read-only display —
        // edit UI is slice C.
        //
        // CTSK-262 — the wrapper div is ALWAYS rendered; entering/
        // exiting fullscreen only toggles its class (and the iframe's
        // height style). The iframe element itself never moves in the
        // tree, so the browser preserves the DOM node and the viz
        // keeps its zoom/selection state across the toggle.
        // CTSK-320 — the viz container ALWAYS renders on the viz tab
        // (even with zero sources selected: the rail is how you reach
        // the drawer to select one). Fixed child order — rail, body,
        // drawer — so every chrome toggle reconciles in place and the
        // iframe node survives drawer + fullscreen changes.
        <div
          data-testid="viz-container"
          className={isFullscreen ? 'timeline-lane-viz-fullscreen' : undefined}
          style={{
            // Normal mode anchors the absolute drawer; fullscreen mode
            // must NOT override the class's position:fixed (inline
            // style would win), and fixed anchors the drawer just the
            // same.
            position: isFullscreen ? undefined : 'relative',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* CTSK-322 — the chip strip, replacing CTSK-320's action rail
              + drawer-for-sources. Left: one chip per source (plus LAB
              chips and `+ Lab`). Right: lock + expand + ⚙. Lives INSIDE
              the container so fullscreen keeps both it and the drawer
              reachable (CTSK-262/320 AC preserved). */}
          <TimelineVizChipStrip
            options={VIZ_SOURCE_OPTIONS}
            allKeys={ALL_VIZ_SOURCE_KEYS}
            onShowAll={showAllVizSources}
            sources={vizSources}
            onToggle={toggleVizSource}
            counts={vizCounts}
            schemaOverrides={schemaOverrides}
            labSources={labSources}
            onLabRemove={handleLabRemove}
            onOpenLab={() => openLabZone('data')}
            onOpenSchemaZone={() => openLabZone('schema')}
            // Same per-visit editUnlocked state as the file tabs
            // (CTSK-268 slice B) — nothing on the viz tab consumes it
            // yet, but the strip keeps the affordance consistent across
            // all six tabs and it re-locks on tab switch as ever.
            locked={!editUnlocked}
            onToggleLock={() => { setEditUnlocked((u) => !u); setShowEditModal(false); setShowCleanModal(false); }}
            isFullscreen={isFullscreen}
            onToggleFullscreen={() => setIsFullscreen(!isFullscreen)}
            drawerOpen={drawerOpen}
            onToggleDrawer={() => setDrawer(!drawerOpen)}
          />

          {vizSources.length === 0 ? (
            // Zero sources selected → no iframe (the viz would just
            // render its "no events loaded" error state); inline hint
            // instead. The strip stays — and it now carries an explicit
            // "Show all 5" chip, so the way back is one click.
            <div style={{ padding: 16, background: 'var(--surface-sunken)', color: 'var(--text-muted)', fontSize: 13 }}>
              No data sources selected — pick one above, or use “Show all {ALL_VIZ_SOURCE_KEYS.length}”.
            </div>
          ) : (
            /* CTSK-261 — `key` forces a full unmount/remount when the
               selection changes, so the viz reloads with the new
               `?sources=`. The default (all 5) still passes the param
               explicitly — same result as the viz's no-param path, but
               the src is deterministic for tests. Fullscreen (CTSK-262)
               and drawer (CTSK-320) toggles don't change the key, so
               the iframe node is preserved across both. */
            <iframe
              key={vizSourcesKey}
              ref={vizFrameRef}
              src={`${VIZ_IFRAME_SRC}?sources=${vizSourcesKey}`}
              title="Timeline visualization"
              style={{
                width: '100%',
                border: 'none',
                display: 'block',
                // Fullscreen: fill the fixed container under the rail
                // (flex column); normal: the CTSK-253 80vh viewport.
                ...(isFullscreen
                  ? { flex: '1 1 auto', minHeight: 0, height: 'auto' }
                  : { height: '80vh' }),
              }}
              loading="lazy"
            />
          )}

          {/* CTSK-320 → CTSK-322 — the drawer. ALWAYS mounted (open/close
              is a transform toggle, never a mount/unmount) and LAST in
              the container so it overlays the iframe from the right. As
              of CTSK-322 it holds chrome preferences only; every source
              control moved up to the chip strip. */}
          <TimelineVizSettingsDrawer isOpen={drawerOpen} onClose={() => setDrawer(false)} />
        </div>
      ) : !activeFile ? (
        <div style={{ padding: 16, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', color: 'var(--text-muted)', fontSize: 13 }}>
          No registry files are configured for this tool. Check <code>storage.registryFiles</code> in the tool manifest.
        </div>
      ) : ptrQ.isLoading || (ptrQ.data?.documentId && yamlQ.isLoading) ? (
        <div style={{ padding: 20, color: 'var(--text-muted)' }}>Loading {activeFile.path}…</div>
      ) : ptrQ.data && !ptrQ.data.documentId ? (
        // Missing-file empty state — per tab. The file is registered
        // in the tool config but hasn't been uploaded into the bound
        // container yet.
        <div style={{ padding: 16, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', color: 'var(--text-muted)', fontSize: 13 }}>
          The <code style={{ fontFamily: 'var(--font-mono)' }}>{activeFile.path}</code> file isn't in this
          container yet. Upload it via the container's file list (or re-run the timeline seed step) — see{' '}
          <code>docs/api-howto.md</code> §21.11 for the container-upload runbook.
        </div>
      ) : (
        renderTable(activeFile.schema, yamlQ.data ?? '', activeFile.path)
      )}

      {/* CTSK-268 slice B — whole-document YAML editor (the slice's
          escalation path: no per-row mutation endpoint exists). Save
          goes through PATCH /documents/:id with If-Match, so a
          concurrent edit 409s instead of silently overwriting. */}
      {showEditModal && !isVizTab && activeFile && ptrQ.data?.documentId && (
        <DocumentYamlEditModal
          documentId={ptrQ.data.documentId}
          path={activeFile.path}
          initialYaml={yamlQ.data ?? ''}
          onClose={() => setShowEditModal(false)}
        />
      )}

      {/* CTSK-256 — cleaning modal (dedupe / normalize / migrate with a
          mandatory dry-run preview before apply). */}
      {showCleanModal && !isVizTab && activeFile && ptrQ.data?.documentId && tool && (
        <CleanRegistryModal
          toolId={tool.id}
          path={activeFile.path}
          onClose={() => setShowCleanModal(false)}
        />
      )}
    </div>
  );
}
